import { inject, Injectable, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CompanyController } from '@domain';
import { EditDataService } from '../pages/edit/services/_edit-data.service';
import { UNSAFE_CHART_SVG } from '../pdf-renderer/helpers';
import { loadChartEcharts } from './chart-echarts';
import { ChartEcharts, ChartRenderOptions, ChartValue, renderChartSvg } from './chart-render';

/**
 * Desenha gráficos na web, com o idioma e a moeda do documento aberto (a mesma resolução
 * da Prévia do PDF e da API), para o SVG local sair igual ao que a API grava.
 *
 * O ECharts chega sob demanda: `ready` liga quando ele terminou de baixar, e quem desenha
 * antes disso recebe '' e redesenha quando `ready` mudar (é um signal).
 */
@Injectable({ providedIn: 'root' })
export class ChartRenderService {
    private _company = inject(CompanyController);
    private _editData = inject(EditDataService);
    private _sanitizer = inject(DomSanitizer);

    private _echarts: ChartEcharts | null = null;
    private _ready = signal(false);
    public readonly ready = this._ready.asReadonly();

    /** Começa a baixar o ECharts (idempotente). Chame ao abrir o assistente ou ver um gráfico. */
    public load(): Promise<void> {
        if (this._echarts) return Promise.resolve();
        return loadChartEcharts().then(echarts => {
            this._echarts = echarts;
            this._ready.set(true);
        });
    }

    /** Idioma e moeda do documento aberto no editor. */
    public documentOptions(): ChartRenderOptions {
        const company = this._company.currentCompany() as
            | { locale?: string; documentConfig?: { locale?: string } }
            | null;
        const config = this._editData.document()?.config;
        return {
            locale: company?.documentConfig?.locale ?? company?.locale ?? config?.locale ?? 'pt-BR',
            currencyCode: config?.currencyCode ?? undefined,
        };
    }

    /** SVG do gráfico ('' enquanto o ECharts não chegou ou sem número nenhum). */
    public render(value: ChartValue, options: ChartRenderOptions = {}): string {
        // Lê o signal para quem chama dentro de um computed redesenhar quando ele chegar.
        if (!this._ready() || !this._echarts) return '';
        return renderChartSvg(this._echarts, value, { ...this.documentOptions(), ...options });
    }

    /**
     * SVG pronto para o template. O SVG é desenhado aqui ou pela API, sempre por
     * `renderChartSvg` a partir de texto escapado pelo próprio ECharts (sem script, sem
     * HTML do usuário): pode entrar como HTML confiável. Qualquer coisa que não pareça um
     * SVG limpo é descartada.
     */
    public trust(svg: string | null | undefined): SafeHtml | null {
        const clean = svg?.trim();
        if (!clean || !clean.startsWith('<svg') || UNSAFE_CHART_SVG.test(clean)) return null;
        return this._sanitizer.bypassSecurityTrustHtml(clean);
    }
}
